import { useEffect, useMemo, useState } from 'react';
import PaymentsAPI from 'api/PaymentsAPI';
import { ChangeItem, getNetProrationAmount } from './PlanConfirmModals';

export interface Subscription {
  id: string;
  subId?: string;
  subItemId?: string;
  priceId?: string;
  name: string;
  accent: string;
  pricePerUnit: number;
  quantity: number;
  renewsOn: string;
  cancelled?: boolean;
  schedule?: string | null;
}

export interface ScheduledDowngrade {
  scheduleId: string;
  targetQuantity: number;
}

// Reused by both ManageSubscriptions.tsx (the "Manage My Subscriptions" modal)
// and SubscriptionPricing.tsx (the plan cards + inline Plan Summary) so an
// owner (canManageBilling) editing an already-active plan's license count gets
// identical upgrade/downgrade/proration behavior no matter where they do it:
// upgrades apply immediately via updateSubQuantity, downgrades are scheduled
// via a Stripe subscription schedule so they only take effect at the next
// billing period (never mid-cycle), and quantity-upgrade proration is
// previewed live so the confirm step can require payment only when there's
// actually a prorated amount due today.
export function useActiveLicenseManager({
  subscriptions: incomingSubscriptions,
  customerId,
  onSubscriptionsUpdated,
}: {
  subscriptions: Subscription[];
  customerId?: string;
  onSubscriptionsUpdated?: () => Promise<void> | void;
}) {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(
    incomingSubscriptions,
  );
  const [pendingQuantities, setPendingQuantities] = useState<
    Record<string, number>
  >(Object.fromEntries(incomingSubscriptions.map((s) => [s.id, s.quantity])));
  const [scheduledDowngrades, setScheduledDowngrades] = useState<
    Record<string, ScheduledDowngrade>
  >({});
  const [
    quantityUpgradeProrationDueToday,
    setQuantityUpgradeProrationDueToday,
  ] = useState<number | null>(null);
  const [cancelRenewModal, setCancelRenewModal] = useState<{
    open: boolean;
    type: 'cancel' | 'renew';
    subId: string;
    planName: string;
    renewsOn: string;
  }>({ open: false, type: 'cancel', subId: '', planName: '', renewsOn: '' });

  useEffect(() => {
    setSubscriptions(incomingSubscriptions);
    setPendingQuantities(
      Object.fromEntries(incomingSubscriptions.map((s) => [s.id, s.quantity])),
    );
  }, [incomingSubscriptions]);

  useEffect(() => {
    let isMounted = true;

    const loadScheduledDowngrades = async () => {
      const activePlans = incomingSubscriptions.filter(
        (sub) => !sub.cancelled && sub.subId,
      );

      if (activePlans.length === 0) {
        if (isMounted) {
          setScheduledDowngrades({});
        }
        return;
      }

      const detected: Record<string, ScheduledDowngrade> = {};

      await Promise.all(
        activePlans.map(async (sub) => {
          const subResp = await PaymentsAPI.getSubscriptionById(
            sub.subId as string,
          );
          if (!subResp?.success) {
            return;
          }

          const scheduleId = subResp?.result?.schedule;
          if (!scheduleId) {
            return;
          }

          const scheduleResp = await PaymentsAPI.getSubSchedule(scheduleId);
          if (!scheduleResp?.success) {
            return;
          }

          const phases = scheduleResp?.result?.phases;
          const currentQty = Number(
            phases?.[0]?.items?.[0]?.quantity ?? sub.quantity,
          );
          const nextQty = Number(
            phases?.[1]?.items?.[0]?.quantity ?? currentQty,
          );

          if (nextQty < currentQty) {
            detected[sub.id] = {
              scheduleId: scheduleResp.result.id,
              targetQuantity: nextQty,
            };
          }
        }),
      );

      if (isMounted) {
        setScheduledDowngrades(detected);
      }
    };

    loadScheduledDowngrades();

    return () => {
      isMounted = false;
    };
  }, [incomingSubscriptions]);

  useEffect(() => {
    const loadQuantityUpgradeProration = async () => {
      if (!customerId) {
        setQuantityUpgradeProrationDueToday(null);
        return;
      }

      const upgrades = subscriptions.filter((sub) => {
        if (sub.cancelled || !sub.subId || !sub.priceId) {
          return false;
        }
        const pendingQty = pendingQuantities[sub.id] ?? sub.quantity;
        return pendingQty > sub.quantity;
      });

      if (upgrades.length === 0) {
        setQuantityUpgradeProrationDueToday(null);
        return;
      }

      const previews = await Promise.all(
        upgrades.map((sub) =>
          PaymentsAPI.previewSubPayment(
            sub.subId as string,
            customerId,
            sub.priceId as string,
            pendingQuantities[sub.id] ?? sub.quantity,
          ),
        ),
      );

      if (previews.some((resp) => !resp?.success)) {
        setQuantityUpgradeProrationDueToday(null);
        return;
      }

      const dueToday = previews.reduce((total, preview) => {
        const amount = getNetProrationAmount(preview?.result);
        if (typeof amount !== 'number') {
          return total;
        }
        return total + Math.max(amount, 0);
      }, 0);

      setQuantityUpgradeProrationDueToday(dueToday);
    };

    loadQuantityUpgradeProration();
  }, [customerId, subscriptions, pendingQuantities]);

  const updateQty = (id: string, delta: number) =>
    setPendingQuantities((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] ?? 0) + delta),
    }));

  const setQtyFromInput = (id: string, value: number) =>
    setPendingQuantities((prev) => ({
      ...prev,
      [id]: Math.max(0, value),
    }));

  const revert = () =>
    setPendingQuantities(
      Object.fromEntries(subscriptions.map((s) => [s.id, s.quantity])),
    );

  const cancelScheduledDowngrade = async (subId: string) => {
    const scheduled = scheduledDowngrades[subId];
    if (!scheduled?.scheduleId) {
      return;
    }
    const releaseResp = await PaymentsAPI.releaseSubSchedule(
      scheduled.scheduleId,
    );
    if (!releaseResp?.success) {
      return;
    }
    setScheduledDowngrades((prev) => {
      const next = { ...prev };
      delete next[subId];
      return next;
    });
    await onSubscriptionsUpdated?.();
  };

  const requestToggleCancel = (sub: Subscription) =>
    setCancelRenewModal({
      open: true,
      type: sub.cancelled ? 'renew' : 'cancel',
      subId: sub.id,
      planName: sub.name,
      renewsOn: sub.renewsOn,
    });

  const closeCancelRenewModal = () =>
    setCancelRenewModal((prev) => ({ ...prev, open: false }));

  const confirmToggleCancel = async () => {
    const targetSub = subscriptions.find(
      (s) => s.id === cancelRenewModal.subId,
    );
    if (targetSub?.subId) {
      if (targetSub.cancelled) {
        await PaymentsAPI.renewCanceledSub(targetSub.subId);
      } else {
        await PaymentsAPI.cancelSubAtPeriodEnd(targetSub.subId);
      }
    }
    setSubscriptions((prev) =>
      prev.map((s) =>
        s.id === cancelRenewModal.subId ? { ...s, cancelled: !s.cancelled } : s,
      ),
    );
    await onSubscriptionsUpdated?.();
  };

  // Applies pending license-quantity changes: upgrades immediately via
  // updateSubQuantity, downgrades via a scheduled subscription change (never
  // mid-cycle). Does not touch credit packs — callers that also manage credit
  // packs (ManageSubscriptions.tsx) apply those separately and merge the
  // results, since they use their own PaymentsAPI.changePlan calls.
  const confirmChanges = async (paymentMethodId?: string): Promise<boolean> => {
    const activeSubs = subscriptions.filter((sub) => !sub.cancelled);
    // Taking a plan to 0 licenses is a cancellation at the end of the billing
    // period (same as the Cancel button) -- Stripe won't hold a subscription
    // item at 0, so it never becomes a quantity change or scheduled downgrade.
    const cancellations = activeSubs.filter(
      (sub) => (pendingQuantities[sub.id] ?? sub.quantity) === 0,
    );
    const downgrades = activeSubs.filter((sub) => {
      const pendingQty = pendingQuantities[sub.id] ?? sub.quantity;
      return pendingQty > 0 && pendingQty < sub.quantity;
    });
    const upgrades = activeSubs.filter((sub) => {
      const pendingQty = pendingQuantities[sub.id] ?? sub.quantity;
      return pendingQty > sub.quantity;
    });

    if (cancellations.length > 0) {
      const cancelResponses = await Promise.all(
        cancellations
          .filter((sub) => Boolean(sub.subId))
          .map((sub) => PaymentsAPI.cancelSubAtPeriodEnd(sub.subId as string)),
      );
      if (cancelResponses.some((resp) => !resp?.success)) {
        return false;
      }
      const cancelledIds = new Set(cancellations.map((sub) => sub.id));
      setSubscriptions((prev) =>
        prev.map((sub) =>
          cancelledIds.has(sub.id) ? { ...sub, cancelled: true } : sub,
        ),
      );
    }

    const updateCalls = upgrades
      .filter((sub) => Boolean(sub.subItemId))
      .map((sub) =>
        PaymentsAPI.updateSubQuantity({
          subItemId: sub.subItemId,
          newQuantity: pendingQuantities[sub.id] ?? sub.quantity,
        }),
      );

    if (updateCalls.length > 0) {
      const responses = await Promise.all(updateCalls);
      const hasFailure = responses.some((resp) => !resp?.success);
      if (hasFailure) {
        return false;
      }
    }

    if (downgrades.length > 0) {
      const nextScheduledDowngrades = { ...scheduledDowngrades };

      for (const downgradeSub of downgrades) {
        const targetQty =
          pendingQuantities[downgradeSub.id] ?? downgradeSub.quantity;

        if (
          !downgradeSub.subId ||
          !downgradeSub.priceId ||
          targetQty >= downgradeSub.quantity
        ) {
          return false;
        }

        const existingSchedule = scheduledDowngrades[downgradeSub.id];
        if (existingSchedule && existingSchedule.targetQuantity === targetQty) {
          nextScheduledDowngrades[downgradeSub.id] = existingSchedule;
          continue;
        }

        if (existingSchedule?.scheduleId) {
          const releaseResp = await PaymentsAPI.releaseSubSchedule(
            existingSchedule.scheduleId,
          );
          if (!releaseResp?.success) {
            return false;
          }
        }

        const scheduleResp = await PaymentsAPI.createSubSchedule(
          downgradeSub.subId,
          {
            oldPrice: downgradeSub.priceId,
            price_id: downgradeSub.priceId,
            quantity: targetQty,
            oldMeterPriceGroup: null,
            newMeterPriceGroup: null,
            oldMetadata: {},
            newMetadata: {},
          },
        );

        if (!scheduleResp?.success || !scheduleResp?.result?.id) {
          return false;
        }

        nextScheduledDowngrades[downgradeSub.id] = {
          scheduleId: scheduleResp.result.id,
          targetQuantity: targetQty,
        };
      }

      setScheduledDowngrades(nextScheduledDowngrades);
    }

    if (paymentMethodId) {
      const subIdsToUpdate = new Set<string>(
        upgrades
          .map((sub) => sub.subId)
          .filter((subId): subId is string => Boolean(subId)),
      );

      if (subIdsToUpdate.size > 0) {
        const paymentUpdates = await Promise.all(
          Array.from(subIdsToUpdate).map((subId) =>
            PaymentsAPI.updateSubPayment(subId, { id: paymentMethodId }),
          ),
        );

        if (paymentUpdates.some((resp) => !resp?.success)) {
          return false;
        }
      }
    }

    setSubscriptions((prev) =>
      prev.map((sub) => ({
        ...sub,
        quantity:
          (pendingQuantities[sub.id] ?? sub.quantity) > sub.quantity
            ? pendingQuantities[sub.id] ?? sub.quantity
            : sub.quantity,
      })),
    );

    setPendingQuantities((prev) => {
      const next = { ...prev };
      subscriptions.forEach((sub) => {
        const pendingQty = prev[sub.id] ?? sub.quantity;
        if (pendingQty < sub.quantity) {
          next[sub.id] = sub.quantity;
        }
      });
      return next;
    });

    await onSubscriptionsUpdated?.();
    return true;
  };

  const changes: ChangeItem[] = useMemo(() => {
    const list: ChangeItem[] = [];
    subscriptions.forEach((sub) => {
      if (sub.cancelled) {
        return;
      }
      const origQty = sub.quantity;
      const newQty = pendingQuantities[sub.id] ?? origQty;
      const scheduledDowngrade = scheduledDowngrades[sub.id];
      if (scheduledDowngrade) {
        list.push({
          type: 'change',
          label: `${sub.name} — downgrade scheduled`,
          detail: `${origQty} → ${scheduledDowngrade.targetQuantity} licenses next billing cycle`,
          amount: `$${sub.pricePerUnit * scheduledDowngrade.targetQuantity}/mo`,
        });
      }
      if (newQty === 0 && origQty > 0) {
        list.push({
          type: 'remove',
          label: `${sub.name} — cancel at end of billing period`,
          detail: `${origQty} → 0 licenses`,
          amount: `$0/mo after ${sub.renewsOn}`,
        });
      } else if (newQty !== origQty) {
        const diff = newQty - origQty;
        list.push({
          type: diff > 0 ? 'add' : 'change',
          label: `${sub.name} — ${diff > 0 ? 'add' : 'remove'} ${Math.abs(
            diff,
          )} license${Math.abs(diff) > 1 ? 's' : ''}`,
          detail: `${origQty} → ${newQty} licenses`,
          amount: `$${sub.pricePerUnit * newQty}/mo`,
        });
      }
    });
    return list;
  }, [subscriptions, pendingQuantities, scheduledDowngrades]);

  const hasPendingChanges = changes.length > 0;
  const newTotal = subscriptions.reduce((sum, sub) => {
    if (sub.cancelled) return sum;
    const qty = pendingQuantities[sub.id] ?? sub.quantity;
    return sum + sub.pricePerUnit * qty;
  }, 0);
  const currentTotal = subscriptions.reduce(
    (sum, sub) => sum + sub.pricePerUnit * sub.quantity,
    0,
  );

  return {
    subscriptions: subscriptions,
    pendingQuantities: pendingQuantities,
    updateQty: updateQty,
    setQtyFromInput: setQtyFromInput,
    revert: revert,
    scheduledDowngrades: scheduledDowngrades,
    cancelScheduledDowngrade: cancelScheduledDowngrade,
    quantityUpgradeProrationDueToday: quantityUpgradeProrationDueToday,
    changes: changes,
    hasPendingChanges: hasPendingChanges,
    newTotal: newTotal,
    currentTotal: currentTotal,
    confirmChanges: confirmChanges,
    cancelRenewModal: cancelRenewModal,
    requestToggleCancel: requestToggleCancel,
    closeCancelRenewModal: closeCancelRenewModal,
    confirmToggleCancel: confirmToggleCancel,
  };
}
